import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ActiveScreenSetPayload,
  getActiveScreen,
  getScreensArray,
  FormsConfigActions,
  validateFormWithPhoneCheck,
} from 'app/store/data/formsConfig';
import { RootState } from 'app/store/rootReducer';
import { FormsEnum, ScreenEnum } from 'app/shared/enums';
import { ScrollService } from 'app/services/scroll.service';
import { StorageService } from 'app/services/storage.service';
import { TagManagerService } from 'app/services/tagManager.service';
import { UtilService } from 'app/services/util.service';
import { DictService } from 'app/services/dict.service';
import { scrollToFirstInvalidField } from 'app/components/Form/scrollToFirstInvalidField';
import { FormProps } from './Form.interfaces';
import { ThunkAnyDispatch } from 'app/store/helpers';

export function useForm({
  formName,
  action,
  screenSubmitAction,
  extraValidation,
}: FormProps) {
  const dispatch = useDispatch<ThunkAnyDispatch>();
  const screens = useSelector((state: RootState) =>
    getScreensArray(state, formName)
  );
  const activeScreen = useSelector((state: RootState) =>
    getActiveScreen(state, formName)
  );
  const setActiveScreen = (payload: ActiveScreenSetPayload) =>
    dispatch(FormsConfigActions.setActiveScreenAction(payload));

  const nextScreen = useCallback(() => {
    const nextScreenIndex = activeScreen + 1;
    const nextScreen = screens[nextScreenIndex];
    const nextScreenTagName = nextScreen.label
      ? nextScreen.label
      : nextScreen.sections[0] || '';

    TagManagerService.addDataLayer({
      event: 'nextStep',
      virtualPageView: UtilService.getVirtualPageView(nextScreenTagName),
    });

    setActiveScreen({
      screen: nextScreenIndex,
      formName,
    });

    DictService.resolveAllDict().finally(() => {
      setTimeout(
        () =>
          requestAnimationFrame(() =>
            ScrollService.scrollToScreen(nextScreenIndex)
          ),
        300
      );
    });
    StorageService.setReachedScreen(formName, nextScreenIndex);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScreen, screens, formName]);

  const onSubmit = useCallback(async () => {
    const screenKey = screens[activeScreen].key as ScreenEnum;
    const lastScreen = screens.length - 1;
    const isLastScreen = activeScreen === lastScreen;

    const formValid = await dispatch(validateFormWithPhoneCheck(formName));

    const isExtraValidation = extraValidation ? extraValidation() : true;

    if (formValid && isExtraValidation) {
      if (isLastScreen) {
        action();
      } else {
        if (screenSubmitAction) {
          try {
            await screenSubmitAction(screenKey);
          } catch (error) {
            nextScreen();
          }
        }

        nextScreen();
      }
    } else {
      if (formName === FormsEnum.CalculationForm) {
        TagManagerService.addDataLayer({
          dataLayer: {
            event: 'formSubmitted',
            validation: false,
          },
        });
      }
      setTimeout(
        () => requestAnimationFrame(() => scrollToFirstInvalidField(formName)),
        0
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScreen, screens, formName, action, nextScreen]);

  return {
    screens,
    activeScreen,
    onSubmit,
  };
}
